import React from 'react';
import { Pagination, PaginationItem, PaginationLink, Input, Label, Row, Col } from 'reactstrap';

const TablePagination = ({
    totalItems,
    itemsPerPage,
    currentPage,
    onPageChange,
    onItemsPerPageChange,
    type,
    totalPages: propTotalPages
}) => {
    // Use the provided totalPages prop if available, otherwise calculate it
    const totalPages = propTotalPages || Math.ceil(totalItems / itemsPerPage);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            // Show all pages if there are fewer than maxPagesToShow
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate start and end of page range
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            // Adjust if we're at the beginning or end
            if (currentPage <= 2) {
                end = Math.min(totalPages - 1, 4);
            } else if (currentPage >= totalPages - 1) {
                start = Math.max(2, totalPages - 3);
            }

            // Add ellipsis if needed
            if (start > 2) {
                pages.push('...');
            }

            // Add middle pages
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis if needed
            if (end < totalPages - 1) {
                pages.push('...');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 mb-2">
            <div className="d-flex align-items-center mb-3 mb-md-0">
                <Label className="me-2 mb-0 fw-bold">Items per page:</Label>
                <Input
                    type="select"
                    value={itemsPerPage}
                    onChange={(e) => onItemsPerPageChange(type, parseInt(e.target.value))}
                    style={{ width: '80px' }}
                    className="me-3 form-select-sm"
                >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </Input>
                <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Showing <strong>{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</strong> - <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> items
                </span>
            </div>

            {totalPages > 1 && (
                <Pagination className="mb-0 pagination-sm">
                    <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                            first
                            onClick={() => onPageChange(type, 1)}
                            aria-label="First Page"
                            style={{ borderRadius: '4px 0 0 4px' }}
                        />
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                            previous
                            onClick={() => onPageChange(type, currentPage - 1)}
                            aria-label="Previous Page"
                        />
                    </PaginationItem>

                    {getPageNumbers().map((page, index) => (
                        <PaginationItem
                            key={index}
                            active={page === currentPage}
                            disabled={page === '...'}
                        >
                            <PaginationLink
                                onClick={() => page !== '...' && onPageChange(type, page)}
                                style={{
                                    fontWeight: page === currentPage ? 'bold' : 'normal',
                                    backgroundColor: page === currentPage ? '#007bff' : 'white'
                                }}
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    ))}

                    <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                            next
                            onClick={() => onPageChange(type, currentPage + 1)}
                            aria-label="Next Page"
                        />
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                            last
                            onClick={() => onPageChange(type, totalPages)}
                            aria-label="Last Page"
                            style={{ borderRadius: '0 4px 4px 0' }}
                        />
                    </PaginationItem>
                </Pagination>
            )}
        </div>
    );
};

export default TablePagination;
